import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  UserChatEndpoints as Endpoints,
  type CreateUserChatRequest,
  type CreateUserChatResponse,
  type GetOrCreateAiUserChatRequest,
  type GetOrCreateAiUserChatResponse,
  type GetUserChatMessagesRequest,
  type GetUserChatMessagesResponse,
  type ListUserChatsRequest,
  type ListUserChatsResponse,
  type SendUserChatMessageRequest,
  type SendUserChatMessageResponse,
  type UserChatMarkAsReadRequest,
  type UserChatMarkAsReadResponse,
  type UserChatResetContextRequest,
  type UserChatResetContextResponse,
  type UserChatTypingRequest,
  type UserChatTypingResponse,
} from '@app/api/UserChat/types';

export const userChatApi = createApi({
  reducerPath: 'userChatApi',
  baseQuery: createApiBaseQuery('/userChat'),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.Create]: mutation<CreateUserChatResponse, CreateUserChatRequest>(
      {
        query: ({ data, ...params }) => ({
          url: Endpoints.Create,
          method: Methods.POST,
          params,
          data,
        }),
      }
    ),
    [Endpoints.List]: query<ListUserChatsResponse, ListUserChatsRequest>({
      query: (params) => ({
        url: Endpoints.List,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SendMessage]: mutation<
      SendUserChatMessageResponse,
      SendUserChatMessageRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SendMessage,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetMessages]: query<
      GetUserChatMessagesResponse,
      GetUserChatMessagesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetMessages,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.Typing]: mutation<UserChatTypingResponse, UserChatTypingRequest>(
      {
        query: ({ data, ...params }) => {
          const formData = new FormData();

          formData.append('chatId', String(data.chatId));

          return {
            url: Endpoints.Typing,
            method: Methods.POST,
            params,
            data: formData,
          };
        },
      }
    ),
    [Endpoints.MarkAsRead]: mutation<
      UserChatMarkAsReadResponse,
      UserChatMarkAsReadRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.MarkAsRead,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetOrCreateAiUserChat]: query<
      GetOrCreateAiUserChatResponse,
      GetOrCreateAiUserChatRequest
    >({
      query: (params) => ({
        url: Endpoints.GetOrCreateAiUserChat,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ResetContext]: mutation<
      UserChatResetContextResponse,
      UserChatResetContextRequest
    >({
      query: ({ data, ...params }) => {
        const formData = new FormData();

        formData.append('chatId', String(data.chatId));

        return {
          url: Endpoints.ResetContext,
          method: Methods.POST,
          params,
          data: formData,
        };
      },
    }),
  }),
});

export const {
  useCreateMutation: useCreateUserChatMutation,
  useListQuery: useListUserChatsQuery,
  useLazyListQuery: useLazyListUserChatsQuery,
  useSendMessageMutation: useSendUserChatMessageMutation,
  useGetMessagesQuery: useGetUserChatMessagesQuery,
  useLazyGetMessagesQuery: useLazyGetUserChatMessagesQuery,
  useTypingMutation: useUserChatTypingMutation,
  useMarkAsReadMutation: useUserChatMarkAsReadMutation,
  useGetOrCreateAiUserChatQuery,
  useLazyGetOrCreateAiUserChatQuery,
  useResetContextMutation: useUserChatResetContextMutation,
} = userChatApi;
